import React from "react";
import {
  Tooltip,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Switch,
  Divider,
} from "@mui/material";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { EditItem } from "@/pages/seller/articles";
import { LANG } from "@/types";
import { NoImage } from "@/assets";
import { Edit3, Trash2, Globe, Tag, Image } from "lucide-react";

interface ProductCardProps {
  item: any;
  onDelete: (id: string) => void;
  setAction: (item: EditItem) => void;
  onPublish: () => void;
  setMediaModalOpen: (item: EditItem) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  item,
  onDelete,
  setAction,
  onPublish,
  setMediaModalOpen,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? "#10b981" : "#6b7280";
  };

  const getStatusBgColor = (isPublished: boolean) => {
    return isPublished ? "#d1fae5" : "#f3f4f6";
  };

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        transition: "all 0.3s ease",
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      {/* Image Section */}
      <Box
        sx={{
          position: "relative",
          height: 200,
          overflow: "hidden",
          backgroundColor: "#f9fafb",
        }}
      >
        <img
          src={
            item.images
              ? item.images[0]
              : item.imagePreview
              ? item.imagePreview
              : NoImage
          }
          alt={item.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        />

        {/* Status Badge */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<Globe size={14} />}
            label={item.isPublished ? t("product_form.isPublished") : "Draft"}
            size="small"
            sx={{
              backgroundColor: getStatusBgColor(item.isPublished),
              color: getStatusColor(item.isPublished),
              fontWeight: 600,
              fontSize: "0.75rem",
              border: `1px solid ${getStatusColor(item.isPublished)}20`,
            }}
          />
        </Box>

        {/* Price Badge */}
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: 12,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<Tag size={14} />}
            label={`${item.price.toFixed(2)} ${t("currency")}`}
            size="small"
            sx={{
              backgroundColor: "rgba(59, 130, 246, 0.9)",
              color: "white",
              fontWeight: 700,
              fontSize: "0.875rem",
              backdropFilter: "blur(8px)",
            }}
          />
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#1f2937",
            mb: 1,
            fontSize: "1.125rem",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.name}
        </Typography>

        {/* Code */}
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            mb: 2,
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          {t("item.fields.code")}: {item.code || "N/A"}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            mb: 3,
            fontSize: "0.875rem",
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
        >
          {locale === LANG.EN ? item.descEn : item.descAr || "No description"}
        </Typography>

        {/* Product Details */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontSize: "0.75rem" }}
            >
              {t("item.fields.type")}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
            >
              {item.type || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontSize: "0.75rem" }}
            >
              {t("item.fields.vat")}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
            >
              {item.vat}%
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontSize: "0.75rem" }}
            >
              {t("item.fields.unit")}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
            >
              {item.unit || "N/A"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Actions Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Publish Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={item.isPublished}
              onChange={onPublish}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#10b981",
                  "&:hover": {
                    backgroundColor: "rgba(16, 185, 129, 0.08)",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#10b981",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "#6b7280", fontSize: "0.75rem" }}
            >
              {item.isPublished ? t("product_form.isPublished") : "Draft"}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={t("actions.edit")} arrow>
              <IconButton
                size="small"
                onClick={() => setAction(item)}
                sx={{
                  backgroundColor: "#dbeafe",
                  color: "#1d4ed8",
                  "&:hover": {
                    backgroundColor: "#bfdbfe",
                    color: "#1e40af",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <Edit3 size={16} />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("actions.delete")} arrow>
              <IconButton
                size="small"
                onClick={() => onDelete(item.id)}
                sx={{
                  backgroundColor: "#fee2e2",
                  color: "#dc2626",
                  "&:hover": {
                    backgroundColor: "#fecaca",
                    color: "#b91c1c",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("supplier_pages.update_product.img")} arrow>
              <IconButton
                size="small"
                onClick={() => setMediaModalOpen(item)}
                sx={{
                  backgroundColor: "#e0f2fe",
                  color: "#1d4ed8",
                  "&:hover": {
                    backgroundColor: "#bfdbfe",
                    color: "#1e40af",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <Image size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
